import {
  BACKLOG_CAP, BACKLOG_FORGIVEN, CLOSURE_FACTOR, CONGESTION_MIN_SPEED,
  CONGESTION_SPACING, DAY_LENGTH, DEMAND_SHIFT_FIRST_DAY,
  DEMAND_SUPPLY_RATIO,
  DISRUPTION_FIRST_DAY, FUEL_FACTOR, GLUT_FACTOR, GOODS, LANE_COST_FACTOR,
  LANE_UPKEEP_FACTOR, MEAN_YIELD, OUTAGE_FACTOR, PRODUCER_BUILD_COST, PRODUCER_UPGRADE_COST,
  PRODUCER_UPGRADE_STEP, RECIPE, REFINERY_RATE, RETIRE_FIRST_DAY,
  ROUTE_COST_PER_PX, SLUMP_FACTOR, SUBSIDY_FACTOR, THROUGHPUT_FIRST_DAY,
  MAX_REGIONS, REGION_BASE_COST,
  TRANSFER_CAP, TRUCK_CAPACITY, TRUCK_COST, TRUCK_REFUND, TRUCK_UPKEEP, TRUST_MIN,
} from "../core/constants";
import type { MilestoneId } from "../core/types";
import { euro, pct, rate } from "./format";

/**
 * Overlay copy per milestone. Player-facing, hence German, hence not in core.
 *
 * Each body says what appeared on the map and what it will cost, because the
 * map has just changed underneath the player and the overlay is the only place
 * that explains why.
 */
export const MILESTONE_TEXT: Record<MilestoneId, { title: string; body: string }> = {
  "verkehr": {
    title: "Verkehr.",
    body:
      "Mine Nordwest liegt näher an einer Stadt als alles, was du bisher hattest, " +
      "und Talstadt wartet auf Nachschub. Auf kurzen Strecken zahlt sich jeder Lkw " +
      "am schnellsten aus — so lange, bis zu viele davon dieselbe Straße benutzen.",
  },
  "umschlag": {
    title: "Umschlag.",
    body:
      "Zwei Produzenten in den entgegengesetzten Ecken, zwei Städte in der Mitte. " +
      "Wer die Ecken direkt bedient, braucht dafür rund die zweieinhalbfache Flotte. " +
      "Das ist der Punkt, an dem Ware unterwegs abgestellt werden will.",
  },
  "weites-land": {
    title: "Weites Land.",
    body:
      "Sechs Knoten auf einen Schlag, und der Karte bleibt nur noch eine Stufe. " +
      "Was hier wächst, ist die Entfernung: von jetzt an entscheidet weniger, " +
      "wie viele Lkw du hast, als wo sie fahren.",
  },
  "veredelung": {
    title: "Veredelung.",
    body:
      "Zwei Umschlagplätze, die nicht nur lagern. Wo Holz und Erz " +
      "zusammenkommen, entsteht Werkzeug — und eine Stadt wartet bereits darauf, " +
      "ohne dass ein einziger Produzent es herstellen könnte.",
  },
};

// ─── Handbuch (RFC-0010) ────────────────────────────────────────────────────

export interface ManualEntry {
  /** Stable key. Not player-facing; used in tests and as a DOM id suffix. */
  readonly id: string;
  readonly title: string;
  /** May contain <strong>; player-facing, hence German. */
  readonly body: string;
  /** null = always visible. Otherwise the milestone that unlocks it. */
  readonly requires: MilestoneId | null;
}

/**
 * The manual, in reading order: the basics first, then one entry per milestone.
 *
 * Every number is interpolated from src/core/constants.ts. A hardcoded "70 €"
 * would be a second source of truth for a balancing value (architecture rule 4)
 * and would start lying at the next tuning pass — test/manual.test.ts checks
 * each of these values against the constant it came from.
 *
 * The milestone entries end with the announcement text verbatim. That is the
 * whole of "the milestone is readable again": one reused string, no log in the
 * save (RFC-0010 §4).
 */
export const MANUAL: readonly ManualEntry[] = [
  {
    id: "strecken",
    title: "Strecken und Lkw",
    requires: null,
    body:
      `Eine Strecke kostet <strong>${ROUTE_COST_PER_PX} €</strong> je Pixel Länge, ` +
      `einmalig. Ein Lkw kostet <strong>${euro(TRUCK_COST)}</strong> beim Kauf und ` +
      `<strong>${euro(TRUCK_UPKEEP)}</strong> an jedem Tag, an dem er dir gehört — ` +
      "ob er fährt oder wartet.<br><br>" +
      "Daraus kommen die zwei Engpässe, um die dieses Spiel geht. Zu wenige Lkw: " +
      "die Stadt bekommt nicht, was sie braucht, ihr Rückstand wächst, sie zahlt " +
      "schlechter. Zu viele: sie stehen vor einem leeren Lager und kosten " +
      "trotzdem. Zwischen beidem liegt kein fester Wert, sondern eine Zahl, die " +
      "sich mit jeder neuen Stadt verschiebt.<br><br>" +
      "<strong>Entfernung kostet Geld, nicht Durchsatz.</strong> Eine lange " +
      "Strecke trägt fast so viel wie eine kurze: sie fasst mehr Lkw, dafür " +
      "braucht jeder länger für eine Runde, und beides hebt sich fast auf. Teuer " +
      "ist sie beim Bau und in der Flotte — im Ergebnis kaum.<br><br>" +
      "Was Nähe trotzdem entscheidet, ist der <strong>Wettlauf um das " +
      "Lager</strong>. Mehrere Strecken an einem Werk teilen sich dieselbe Ware, " +
      "und wer kürzer fährt, ist öfter da. Eine Stadt, die von jedem Werk ihrer " +
      "Ware weit weg liegt, verliert dieses Rennen dauerhaft: sie zu versorgen " +
      "braucht mehrere Zufahrten, und ganz satt wird sie selten.<br><br>" +
      "Führt eine Strecke an einem <strong>Werk deiner Ware</strong> vorbei, hält " +
      "der Lkw dort und lädt auf, wenn er noch Platz hat — ist nichts da, fährt " +
      "er durch. Das lohnt sich genau dann, wenn deine Lkw halb leer losfahren, " +
      "weil sie sich eine Quelle teilen. Der einzelne Lkw ist am ersten Werk " +
      "ohnehin voll und gewinnt nichts.<br><br>" +
      "Einen zu viel gekauften Lkw gibst du wieder ab: er bringt " +
      `<strong>${euro(Math.round(TRUCK_REFUND * TRUCK_COST))}</strong> zurück, ` +
      "und der Unterhalt hört noch am selben Tag auf. Die Strecke bleibt stehen. " +
      "Was er gerade geladen hat, ist weg — abgeben lohnt sich vor der Fahrt, " +
      "nicht auf halbem Weg.",
  },
  {
    // RFC-0016. Vor „vertrauen", weil der Rückstand seit dieser Änderung eine
    // Ware hat und die Seite danach sonst von etwas redet, das noch niemand
    // eingeführt hat. Der Bedarfswechsel steht am Ende derselben Seite statt in
    // einer eigenen: er ist kein zweites Thema, sondern was mit dem ersten
    // passiert, wenn man lange genug spielt.
    id: "waren",
    title: "Wer was will",
    requires: null,
    body:
      "Jede Stadt will eine <strong>bestimmte Ware</strong>. Zentralstadt wartet " +
      "auf Holz oder auf Erz, nicht auf Nachschub im Allgemeinen — und was sie " +
      "nicht will, wartet sie auch nicht ab.<br><br>" +
      "Abgewiesen wird trotzdem nichts. Ein Lkw mit der falschen Ware wird " +
      `entladen und bezahlt, aber die ganze Ladung zählt als Überschuss und ` +
      `bringt nur <strong>${pct(GLUT_FACTOR)}</strong> des Preises. Eine kurze ` +
      "Strecke zur falschen Stadt ist deshalb selten billiger als eine lange zur " +
      "richtigen: sie ist nur schneller nutzlos.<br><br>" +
      "Der Rückstand zählt ebenfalls je Ware, und jede Ware staut sich bis " +
      `<strong>${BACKLOG_CAP} Einheiten</strong> für sich allein. Das Vertrauen ` +
      "einer Stadt hängt an der <strong>Summe</strong> — eine Stadt, die auf zwei " +
      "Waren wartet, kann dir also doppelt so schnell entgleiten.<br><br>" +
      "Damit zählt wieder, <em>wo</em> etwas steht. Ein Sägewerk ist so viel wert " +
      "wie die Holzstadt in seiner Nähe, und zwei Städte, die dasselbe Sägewerk " +
      "wollen, teilen sich sein Lager — wer näher dran ist, ist zuerst dran.<br><br>" +
      `<strong>Und der Bedarf dreht sich.</strong> Ab Tag ` +
      `<strong>${DEMAND_SHIFT_FIRST_DAY}</strong> kann es passieren, selten — ` +
      "vorher nicht, damit die Karte erst einmal gelernt werden kann. Es ist " +
      "immer ein <strong>Tausch zwischen zwei Städten</strong>: die eine will, " +
      "was die andere wollte. Insgesamt braucht das Land also nie mehr von einer " +
      "Ware, als es davon herstellt — nur woanders.<br><br>" +
      "Der <strong>Rückstand bleibt liegen</strong>. Eine Stadt, die von Holz auf " +
      "Erz wechselt, behält das Holz, das ihr geschuldet war: es wächst nicht " +
      "weiter, zahlt aber weiter vollen Preis, und es zählt weiter gegen ihr " +
      "Vertrauen. Das ist die Frist, in der du die Strecke umhängen kannst — ein " +
      "Wechsel ist ein Rückschlag, keine Wand.",
  },
  {
    // RFC-0017. An keinen Meilenstein gebunden: beide Knöpfe stehen auf der
    // Produzentenkarte, sobald das Geld reicht. Nach „waren", weil die Regel
    // darunter — Nachfrage nur dort, wo sie schon ist — erst Sinn ergibt, wenn
    // klar ist, dass eine Stadt eine bestimmte Ware will.
    id: "wachstum",
    title: "Die Karte wächst weiter",
    requires: null,
    body:
      "Auf jeder Produzentenkarte stehen zwei Knöpfe, und beide kaufen " +
      "dasselbe: mehr Ware <em>und</em> die Städte, die sie haben wollen.<br><br>" +
      `<strong>Ausbauen</strong> hebt ein Werk um ` +
      `<strong>${PRODUCER_UPGRADE_STEP.toFixed(1)} Einheiten je Sekunde</strong> und ` +
      `kostet beim ersten Mal ${euro(PRODUCER_UPGRADE_COST)}. Jedes weitere Mal am ` +
      "<em>selben</em> Werk wird teurer — irgendwann ist das nächste Werk das " +
      "billigere. Und es geht nur so weit, wie eine <strong>einzelne Straße</strong> " +
      "zu einer Stadt, die diese Ware will, das Lager überhaupt leerfahren kann. " +
      "Sagt die Karte, die Straße sei der Engpass, ist mehr Produktion an " +
      "dieser Stelle nichts wert.<br><br>" +
      `<strong>Neues Werk</strong> kostet ${euro(PRODUCER_BUILD_COST)}, und der ` +
      "Platz gehört nicht dir: das Land sucht ihn aus, nach denselben Regeln, " +
      "nach denen jeder Meilenstein seine Knoten gesetzt hat — im Streifen am " +
      "Rand, weit genug von allem anderen. Was du wählst, ist die " +
      "<strong>Ware</strong>.<br><br>" +
      "Der Teil, der beides zusammenhält: Nachfrage entsteht <strong>nur</strong> " +
      "dadurch, dass du Produktion kaufst, und immer im selben Augenblick. " +
      `Aus einer neuen Einheit je Sekunde werden ${pct(DEMAND_SUPPLY_RATIO)} ` +
      "davon an neuem Bedarf, verteilt auf die Städte, die diese Ware schon " +
      "wollen. Ohne diese Kopplung würdest du dafür bezahlen, deine eigene " +
      "Ladung zu entwerten — mehr Ware, gleich viele Münder, alles Weitere " +
      "Überschuss.<br><br>" +
      "Umgekehrt wächst von selbst gar nichts. Eine Stadt, die gut bedient " +
      "wird, will deshalb nicht mehr; sie wartet, bis irgendwo ein Werk " +
      "entsteht, das sie mitversorgt. Wer nichts kauft, spielt dieselbe Karte " +
      "wie am ersten Tag.<br><br>" +
      `<strong>Und das Land nimmt sich auch etwas.</strong> Ab Tag ` +
      `<strong>${RETIRE_FIRST_DAY}</strong> — wenn die Karte fertig gewachsen ` +
      "ist — kann ein Werk <strong>stillgelegt</strong> werden. Selten, höchstens " +
      "eines am Tag, und es ist die einzige Störung, die nicht von selbst wieder " +
      "aufhört.<br><br>" +
      "Es verschwindet nicht: Name, Platz und deine Strecken dorthin bleiben " +
      "stehen, das Lager läuft leer, und deine Lkw warten davor mit dem " +
      "<strong>!</strong>. Diese Strecken abzureißen ist deine Entscheidung — " +
      "so lange sie stehen, kosten sie Unterhalt. Wiederbeleben lässt sich ein " +
      "stillgelegtes Werk nicht; die Antwort darauf ist ein neues woanders.<br><br>" +
      "Die Nachfrage geht mit. Was das Werk produziert hat, wollen die Städte " +
      "danach nicht mehr — sonst stünde die Karte über dem, was sie noch " +
      "herstellt. Was dir bis dahin geschuldet war, bleibt dir geschuldet. Und " +
      "nie trifft es die <em>letzte</em> Quelle einer Ware, nie so viel, dass " +
      "ein Ausfall die Karte unlösbar macht, und nie so, dass eine Stadt " +
      "unerreichbar wird.",
  },
  {
    id: "vertrauen",
    title: "Rückstand und Vertrauen",
    requires: null,
    body:
      "Was eine Stadt nachfragt und nicht bekommt, sammelt sich als " +
      `<strong>Rückstand</strong>. Bis <strong>${BACKLOG_FORGIVEN} Einheiten</strong> ` +
      "verzeiht sie das. Darüber sinkt ihr <strong>Vertrauen</strong>, und Vertrauen " +
      `ist ein Preisfaktor: bei ${pct(TRUST_MIN)} zahlt sie nur noch die Hälfte für ` +
      "dieselbe Ladung.<br><br>" +
      "Vertrauen folgt dem Rückstand verzögert — es fällt nicht sofort, und es " +
      "kommt nicht sofort zurück. Eine Stadt, die du eine Weile hast hungern " +
      "lassen, zahlt auch nach der ersten Lieferung noch schlecht.<br><br>" +
      "Auf jeder Karte bleiben ein paar Städte dauerhaft unten, weil sie zu weit " +
      "von den Werken ihrer Ware liegen. Das ist keine Panne, sondern eine " +
      "Entscheidung: eine Stadt zum halben Preis ist immer noch Umsatz, und die " +
      "Lkw, die sie satt machen würden, fehlen dann woanders.",
  },
  {
    id: "ueberschuss",
    title: "Überschuss",
    requires: null,
    body:
      "Eine Ladung ist wert, was die Stadt tatsächlich erwartet hat. Was über " +
      `ihren Rückstand hinausgeht, bringt nur <strong>${pct(GLUT_FACTOR)}</strong> ` +
      "des Preises.<br><br>" +
      "Nicht null: ein Lkw, der schon unterwegs ist, soll kein Fehler sein, den du " +
      "nicht mehr zurücknehmen kannst. Aber genug Unterschied, dass die Strecke, " +
      "die gebraucht wird, auch die Strecke ist, die zahlt. Wer auf den Rückstand " +
      "der Städte schaut, statt überall gleich viele Lkw hinzustellen, verdient " +
      "messbar mehr.",
  },
  {
    id: "stoerungen",
    title: "Störungen",
    requires: null,
    body:
      `Ab Tag <strong>${DISRUPTION_FIRST_DAY}</strong> kann das Wetter des Marktes ` +
      "umschlagen — vorher nicht, damit die ersten Tage sagen, was ein Lkw " +
      "wirklich kostet.<br><br>" +
      "Beide Sorten drehen an derselben Schraube — am <strong>Unterhalt</strong>, " +
      "den jeder Lkw jeden Tag kostet. Der Lieferpreis bleibt unangetastet.<br><br>" +
      `<strong>Treibstoff.</strong> Der Preis steigt, der Unterhalt liegt für ein ` +
      `paar Tage rund ${pct(FUEL_FACTOR - 1)} höher. Das trifft die ganze Flotte, ` +
      "auch die Lkw, die gerade vor einem leeren Lager stehen. Eine Überkapazität, " +
      "die sonst nur langsam wehtut, wird plötzlich teuer.<br><br>" +
      `<strong>Ausgleich.</strong> Niedrigwasser, die Binnenschifffahrt liegt still, ` +
      `und die Straße wird entschädigt: der Unterhalt sinkt um etwa ` +
      `${pct(1 - SUBSIDY_FACTOR)}. Vorsicht — billige Lkw sind eine Versuchung. ` +
      "Wer in diesen Tagen die Flotte aufbläst, hat sie danach noch, zum alten Preis.<br><br>" +
      "Beides gilt für das ganze Netz, beides läuft nach wenigen Tagen aus, und " +
      "beides steht oben in der Kopfzeile, solange es läuft. Und nichts davon ist " +
      "willkürlich: dieselbe Welt bringt immer dasselbe Wetter am selben Tag.",
  },
  {
    id: "durchsatz",
    title: "Ausfall, Einbruch, Sperrung",
    requires: null,
    body:
      `Ab Tag <strong>${THROUGHPUT_FIRST_DAY}</strong> trifft es nicht mehr nur die ` +
      "Kasse, sondern den Betrieb. Anders als beim Wetter des Marktes hat das hier " +
      "eine <strong>Adresse</strong>: es steht oben, wen es erwischt hat.<br><br>" +
      `<strong>Ausfall.</strong> Ein Produzent schafft für ein paar Tage nur noch ` +
      `${pct(OUTAGE_FACTOR)} seiner Menge. Die Lkw, die ihn leergefahren haben, ` +
      "stehen jetzt reihum vor einem halb vollen Lager.<br><br>" +
      `<strong>Einbruch.</strong> Eine Stadt braucht nur noch ${pct(SLUMP_FACTOR)} ` +
      "so viel. Ihr Rückstand schmilzt, und was du weiter hinfährst, ist Überschuss " +
      "zum Überschusspreis. Die Flotte, die gestern richtig war, ist heute zu groß.<br><br>" +
      `<strong>Sperrung.</strong> Eine Strecke lässt nur noch ${pct(CLOSURE_FACTOR)} ` +
      "Tempo zu. Dieselben Lkw, weniger Durchsatz — die Flotte, die gestern richtig " +
      "war, ist heute zu klein. Mehr Lkw helfen hier nicht, die Strecke ist eng, " +
      "nicht leer.<br><br>" +
      "Immer nur <strong>eine</strong> davon gleichzeitig, und nie so viel, dass die " +
      "Karte unlösbar wird: die Gesamtproduktion liegt bewusst über dem, was alle " +
      "Städte zusammen fordern, und ein Ausfall darf diesen Abstand nicht aufbrauchen.",
  },
  {
    id: "verkehr",
    title: "Verkehr und Stau",
    requires: "verkehr",
    body:
      `Eine Strecke trägt einen Lkw je <strong>${CONGESTION_SPACING} Pixel</strong> ` +
      "Länge, plus einen. Bis dahin fährt jeder mit voller Geschwindigkeit. " +
      "Darüber wird es <em>quadratisch</em> langsamer für alle auf dieser Straße, " +
      `bis zum Boden bei ${pct(CONGESTION_MIN_SPEED)}.<br><br>` +
      "Der entscheidende Teil: der <strong>Durchsatz der ganzen Strecke</strong> hat " +
      "sein Maximum auf dieser Kapazität und fällt danach. Der Lkw zu viel " +
      "verdient nicht bloß nichts — er nimmt den anderen etwas weg. Eine " +
      "einfärbende Strecke ist die Warnung davor; eine zweite Strecke zur " +
      "Nachbarstadt ist die Antwort.<br><br>" +
      "Die Kapazität ist eine <em>Kommazahl</em>, und dorthin darf man " +
      "<strong>aufrunden</strong>: trägt eine Strecke 2,6 Lkw, bewegen drei mehr " +
      "als zwei — der dritte fährt langsamer, aber die Strecke schafft " +
      "insgesamt mehr. Erst der vierte nimmt weg.<br><br>" +
      "Ob sich das <strong>bezahlt</strong> macht, entscheidet die Quelle, nicht " +
      `die Strecke. Ein Lkw kostet ${euro(TRUCK_UPKEEP)} am Tag und muss dafür ` +
      `rund ${rate(TRUCK_UPKEEP / (DAY_LENGTH * GOODS.holz.price * MEAN_YIELD))} ` +
      "Holz zusätzlich tragen — mehr, als der Listenpreis vermuten lässt, weil " +
      "eine Ladung im Schnitt nur " +
      `${pct(MEAN_YIELD)} davon einbringt: was niemand erwartet hat, zahlt ` +
      `${pct(GLUT_FACTOR)}, und ein enttäuschtes Vertrauen zieht vom Rest noch ` +
      "einmal ab. Ein <em>Werk</em> liefert das selten nach: seine Rate " +
      "ist auf das gedeckelt, was eine Straße wegschafft, und der erste Lkw holt " +
      "davon auf kurzer Strecke fast alles. An einer " +
      "<em>Umschlagplatz-Ausfahrt</em> ist es umgekehrt — dort laufen mehrere " +
      "Zuflüsse zusammen, dort ist die Straße wirklich eng, und dort zahlt " +
      "Aufrunden fast immer.<br><br>" +
      "Die andere Antwort ist die <strong>Spur</strong>. Sie verdoppelt, was die " +
      "Strecke trägt, bevor sie staut — und <em>nur</em> das: unterhalb der " +
      "Kapazität ändert eine zweite Spur nichts, weil dort niemand ausgebremst " +
      `wird. Sie kostet ${pct(LANE_COST_FACTOR)} dessen, was die Lkw kosten, für ` +
      `die sie Platz schafft, und jeden Tag ${pct(LANE_UPKEEP_FACTOR)} von deren ` +
      "Unterhalt. Ausbauen ist deshalb nie pauschal richtig: erst wenn die " +
      "Strecke voll ist und der nächste Lkw den anderen etwas wegnähme, bringt " +
      "die Spur mehr als er.<br><br>" +
      "Eine Spur trägt aber noch etwas Zweites: <strong>das Werk am anderen " +
      "Ende darf mitwachsen.</strong> Wie weit sich ein Werk ausbauen lässt, " +
      "hängt daran, was die Straße zu einer Stadt wegschafft, die seine Ware " +
      "will — und das zählt die <em>schmalste</em> Stelle der Strecke. Wer " +
      "verbreitert, hebt damit beide Decken auf einmal, und wem das Werk den " +
      "Ausbau verweigert, dem sagt die Straße gerade, dass sie zuerst dran " +
      "ist.<br><br>" +
      MILESTONE_TEXT["verkehr"].body,
  },
  {
    id: "umschlag",
    title: "Umschlagplätze",
    requires: "umschlag",
    body:
      "Ein Umschlagplatz erzeugt nichts und verbraucht nichts. Er ist ein Lager " +
      `mit <strong>${TRANSFER_CAP} Einheiten</strong> Platz — für <em>beide</em> Waren ` +
      "zusammen, nicht je Ware. Ein Platz voller Erz hat keinen Raum mehr für Holz.<br><br>" +
      "Er blockiert in zwei Richtungen, und beides ist dasselbe " +
      "<strong>!</strong> am Lkw: leer lässt er den abgehenden Lkw beim Laden " +
      "stehen, voll den ankommenden beim Entladen. Entladen ist alles oder " +
      `nichts — passen die ${TRUCK_CAPACITY} Einheiten nicht mehr ins Lager, ` +
      "wartet der Lkw komplett.<br><br>" +
      "Was ein Platz dir kauft, ist Kapital, nicht Durchsatz: zwei kurze Strecken " +
      "über die Mitte kosten weniger als eine lange aus der Ecke, und jede von " +
      "ihnen hat ihre eigene Kapazität, bevor sie staut.<br><br>" +
      MILESTONE_TEXT["umschlag"].body,
  },
  {
    id: "weites-land",
    title: "Weites Land",
    requires: "weites-land",
    body:
      "Sechs Knoten auf einmal, die größte Erweiterung des Spiels. Es gibt " +
      "keinen Sieg und keinen Bankrott — das Netz läuft weiter, so lange du " +
      "hinsiehst.<br><br>" +
      MILESTONE_TEXT["weites-land"].body,
  },
  {
    id: "veredelung",
    title: "Veredelung",
    requires: "veredelung",
    body:
      "Zwei neue Umschlagplätze sind <strong>Raffinerien</strong>: liegen Holz " +
      `und Erz zusammen in einem, macht er daraus Werkzeug — ${RECIPE.holz} Holz ` +
      `und ${RECIPE.erz} Erz je Stück, bis zu ${REFINERY_RATE} Einheiten in der ` +
      "Sekunde.<br><br>" +
      `Ein Werkzeug bringt ${euro(GOODS.werkzeug.price)} statt der ` +
      `${euro(GOODS.holz.price + GOODS.erz.price)}, die seine beiden Zutaten ` +
      "einzeln gebracht hätten. Dafür räumt es auch nur <em>eine</em> Einheit " +
      "Rückstand ab, wo zwei Rohladungen zwei geräumt hätten. Wer alles " +
      "veredelt, verdient mehr pro Fuhre und lässt die Städte hungern — und " +
      "Vertrauen ist der Faktor auf genau den Preis, der das Veredeln " +
      "attraktiv gemacht hat.<br><br>" +
      `Der Platz dafür ist derselbe wie zum Lagern — aber je Zutat höchstens ` +
      `<strong>${TRANSFER_CAP / Object.keys(RECIPE).length} Einheiten</strong>, ` +
      "damit die eine der anderen nie den Raum wegnimmt. Ein Zulauf, der mehr " +
      "Holz bringt, als die Raffinerie verarbeitet, läuft gegen diese Hälfte " +
      "und seine Lkw warten — auf der Karte ist das dasselbe " +
      "<strong>!</strong> wie an einem vollen Umschlagplatz.<br><br>" +
      `Rechts auf der Karte erschließt du bis zu <strong>${MAX_REGIONS}</strong> Regionen. ` +
      `Die nächste kostet <strong>${euro(REGION_BASE_COST)}</strong> mal ihre Regionsnummer und bringt zwei Werke und zwei Städte. ` +
      "Der Regler unter der Karte springt zwischen den Regionen; Ziehen und Aufziehen bleiben die freie Navigation.<br><br>" +
      MILESTONE_TEXT["veredelung"].body,
  },
];

/**
 * What this world may read. Order follows MANUAL, not the order the milestones
 * were reached — a manual that reshuffles itself is harder to re-read.
 */
export function manualEntries(reached: readonly MilestoneId[]): ManualEntry[] {
  return MANUAL.filter((e) => e.requires === null || reached.includes(e.requires));
}

/**
 * The page a milestone unlocked, so the announcement can open *that* one instead
 * of the whole manual (RFC-0013).
 *
 * A function rather than an expression in the click handler, because it is the
 * one piece of this that is testable without a DOM — and test/manual.test.ts
 * asserts it is total, so a fourth milestone without an entry fails a test
 * instead of opening an empty page.
 */
export const entryFor = (id: MilestoneId): ManualEntry | null =>
  MANUAL.find((e) => e.requires === id) ?? null;
